import { Button, Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ArrowUturnLeftIcon } from '@heroicons/react/16/solid';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { updateObjective } from '../../store/map3PendingSlice';
import { setHighlightedHumanUuid, setHighlightedObjective, setHighlightedIntervalEnding, setHighlightedResultUuid } from '../../store/map3UISlice';
import { AddEditDeleteBar1 } from './AddEditDeleteBar1';
import { ObjectivePager } from './ObjectivePager';
import { ResultPager } from './ResultPager';
import { BadgeButton } from '../../components_catalyst/badge';
import { Toast } from '../../components_misc/Toast';
import { useCurrentMap } from '../../hooks/useCurrentMap';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '@mm-shared/authz';
import { DrawerTabBar, DrawerContext } from './DrawerTabBar';
import { ResultFlag } from './ResultFlag';
import { createResult1 } from '@mm-shared/types/Result1.utils';
import { PARAM_FROM, PARAM_FROM_INTERVAL, getValidReturnTab, getReturnLinkAriaLabel } from './resultDrawerNav';
// Tab components rendered conditionally based on ?tab= param
import { ObjectiveInfoTab } from '../dev/ObjectiveInfoTab';
import { StatusTab1 } from './StatusTab1';
import { ResultsTab } from './ResultsTab';
import { ResultTab } from '../dev/ResultTab';
import { DescendantsTab } from './DescendantsTab';
import { CommentsTab } from './CommentsTab';
import { SettingsTab } from '../dev/SettingsTab';
import { Map3Team } from '../dev/Map3Team';

type Map3DrawerProps = {
  children: React.ReactNode;
  milestoneMapUuid: string;
  ownerName: string;
};


export function Map3Drawer({ children, milestoneMapUuid, ownerName }: Map3DrawerProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { mapShortId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive context from query params (replaces useParams for objectiveShortId/resultShortId)
  const objectiveShortId = searchParams.get('objectiveId') ?? undefined;
  const resultShortId = searchParams.get('resultId') ?? undefined;
  const activeTab = searchParams.get('tab') ?? 'info';
  const drawerParam = searchParams.get('drawer');

  // Derive context from search params
  const drawerContext: DrawerContext = resultShortId ? 'result' : 'objective';
  const isOnResultRoute = drawerContext === 'result';

  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [isEditingObjective, setIsEditingObjective] = useState(false);
  const [objectiveText, setObjectiveText] = useState('');
  const [isActionBarVisible, setIsActionBarVisible] = useState(false);
  const [objectiveCopiedConfirm, setObjectiveCopiedConfirm] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Result header editing state
  const [isEditingResult, setIsEditingResult] = useState(false);
  const [resultText, setResultText] = useState('');
  const [isResultActionBarVisible, setIsResultActionBarVisible] = useState(false);
  const resultTitleInputRef = useRef<HTMLInputElement>(null);
  const [shortIdToastMessage, setShortIdToastMessage] = useState<string | null>(null);

  // Ref for the drawer panel — used to detect outside-clicks and manage focus.
  // panelRef is attached to <DialogPanel> so the capture-phase pointerdown
  // listener can distinguish clicks inside the panel from backdrop clicks.
  const panelRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  // Local ref: stores intended click coordinates so we can identify the cell
  // AFTER the Dialog unmounts. While the drawer is open, <div id="root"> has
  // the `inert` attribute applied by HUI, which removes the grid from
  // hit-testing AND makes <td>s un-focusable. We capture coordinates during
  // the click and defer the .focus() call until inert is removed.
  const intendedClickRef = useRef<{ x: number; y: number } | null>(null);

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.shortId === objectiveShortId),
  );
  const highlightedObjectiveUuid = useSelector(
    (state: RootState) => state.map3UI.highlightedObjectiveUuid,
  );
  const highlightedIntervalEnding = useSelector(
    (state: RootState) => state.map3UI.highlightedIntervalEnding,
  );
  const highlightedResultUuid = useSelector(
    (state: RootState) => state.map3UI.highlightedResultUuid,
  );
  const isHighlighted = !!(objective && highlightedObjectiveUuid === objective.uuid);

  const result = objective?.results.find((r) => r.shortId === resultShortId);
  const isResultHighlighted = !!(result && highlightedResultUuid === result.uuid);

  // Check user's role for edit permissions
  const currentMap = useCurrentMap();
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit, canViewAllResults, canViewComments } =
    getPermissionsForRole(userRole);

  // Feature flags for conditional tab rendering
  const featureFlags = currentMap?.featureFlags;

  // Open drawer when ?drawer=objective is set in the URL
  useEffect(() => {
    if (drawerParam === 'objective' && objectiveShortId) {
      setDrawerIsOpen(true);
    } else if (!drawerParam && !objectiveShortId) {
      setDrawerIsOpen(false);
    }
  }, [drawerParam, objectiveShortId]);

  // Focus management: move focus to first focusable element on context switch or tab change.
  // activeTab is included so that switching tabs (e.g., info → result → status) also moves
  // focus to the close button, mirroring the mr-aqs.12 pattern for drawer context changes.
  useEffect(() => {
    if (drawerIsOpen && firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }
  }, [drawerContext, drawerIsOpen, activeTab]);

  // Capture-phase pointerdown listener — closes the drawer when the user clicks
  // outside the DialogPanel while it is open.
  //
  // Why capture phase: runs before any bubble-phase handler (including HUI's
  // internal click-outside listener), guaranteeing we act before HUI.
  //
  // Why this approach instead of HUI's built-in click-outside:
  //   The two backdrop divs (<div className="fixed inset-0" />) now have
  //   pointer-events-none (see JSX below). Without a real backdrop intercepting
  //   clicks, HUI never sees an outside-click event. We handle it here instead.
  //
  // Guard: if the click target is inside panelRef, we do nothing — the user is
  // interacting with drawer controls (tabs, buttons, inputs, etc.).
  useEffect(() => {
    if (!drawerIsOpen) return;

    const handlePointerDown = (e: PointerEvent) => {
      if (panelRef.current && panelRef.current.contains(e.target as Node)) {
        // Click inside the panel — let it through normally.
        return;
      }
      // Portal guard (INV-DRAWER-INTERVAL): Headless UI renders Listbox options in a
      // floating portal (#headlessui-portal-root) outside the DialogPanel DOM subtree.
      // Without this guard, selecting a "Status as of" interval option fires a pointerdown
      // that the listener interprets as an outside-panel click, closing the drawer.
      // By checking the portal root, we treat Listbox-option clicks as inside-panel.
      const portalRoot = document.getElementById('headlessui-portal-root');
      if (portalRoot && portalRoot.contains(e.target as Node)) {
        // Click inside a Headless UI floating portal (e.g. Listbox options dropdown).
        // Treat as inside-panel: do NOT close the drawer.
        return;
      }
      // Click outside the panel — record coordinates for post-close focus restore,
      // then close the drawer.
      // We capture clientX/clientY NOW because after handleClose() the Dialog
      // unmounts and we can no longer query the element at these coordinates
      // (HUI will apply `inert` to #root, blocking hit-testing). The coordinates
      // are consumed by the post-close useEffect below after inert is removed.
      intendedClickRef.current = { x: e.clientX, y: e.clientY };
      handleClose();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerIsOpen]);

  // Effect: after the drawer closes AND HUI removes the `inert` attribute from
  // #root, find the cell at the captured coordinates and focus it.
  //
  // Why this is needed (Option C, mr-aqs.30 4th attempt):
  //   While the drawer is open, HUI Dialog applies the HTML `inert` attribute
  //   to <div id="root">, which (a) removes every descendant from hit-testing
  //   and (b) makes imperative .focus() on any descendant silently fail.
  //   pointer-events-none on the backdrop (Option A) routes the pointerdown
  //   event to us, but the <td> is STILL inert — we can't focus it yet.
  //   We must wait until HUI removes `inert` before calling td.focus().
  //
  //   The MutationObserver approach fires the moment the attribute is removed,
  //   which is much more reliable than a fixed setTimeout.
  useEffect(() => {
    if (drawerIsOpen) return;
    const coords = intendedClickRef.current;
    if (!coords) return;
    intendedClickRef.current = null; // consume so it doesn't fire again

    const root = document.getElementById('root');
    if (!root) return;

    const focusCellAtCoords = () => {
      const tds = document.querySelectorAll<HTMLTableCellElement>('td[data-row][data-col]');
      const clicked = Array.from(tds).find((td) => {
        const r = td.getBoundingClientRect();
        return (
          coords.x >= r.left &&
          coords.x <= r.right &&
          coords.y >= r.top &&
          coords.y <= r.bottom
        );
      });
      if (!clicked) return;

      clicked.focus({ preventScroll: true });

      // HUI Dialog's FocusTrap has a RestoreFocus feature that fires when the Dialog's
      // close CSS transition completes (~500ms after close). It moves focus back to the
      // element that was focused before the dialog opened (typically the TABLE element,
      // because <td tabIndex=-1> is excluded from HUI's focusableSelector).
      //
      // Guard: install a capture-phase 'focus' listener. The first time focus moves
      // AWAY from our clicked TD (i.e., HUI's late restoration fires), we immediately
      // re-focus the TD and remove the guard. After the first interception the guard is
      // gone — the user can then navigate freely.
      //
      // We also remove the guard after 1200ms regardless (safety cleanup).
      const onFocusStolen = (e: FocusEvent) => {
        const target = e.target as HTMLElement;
        if (target === clicked) return; // still on our cell — no action needed
        // Focus moved elsewhere (likely HUI FocusTrap RestoreFocus on transition end)
        // — intercept once, re-focus our TD, then remove the guard.
        document.removeEventListener('focus', onFocusStolen, true);
        clicked.focus({ preventScroll: true });
      };
      document.addEventListener('focus', onFocusStolen, { capture: true });
      window.setTimeout(() => document.removeEventListener('focus', onFocusStolen, true), 1200);
    };

    if (!root.hasAttribute('inert')) {
      // Already non-inert (e.g., HUI closed immediately) — focus after HUI cleanup.
      setTimeout(focusCellAtCoords, 0);
      return;
    }

    const obs = new MutationObserver(() => {
      if (!root.hasAttribute('inert')) {
        obs.disconnect();
        // HUI Dialog restores focus when closing (via its own rAF/setTimeout logic).
        // We must run AFTER HUI's focus restoration to ensure our td.focus() wins.
        // setTimeout(fn, 0) fires as a macrotask after all pending rAFs and microtasks,
        // which guarantees we run after HUI's focus cleanup.
        setTimeout(focusCellAtCoords, 0);
      }
    });
    obs.observe(root, { attributes: true, attributeFilter: ['inert'] });

    // Safety timeout — give up after 1.5s so we don't leak the observer.
    const timeoutId = window.setTimeout(() => obs.disconnect(), 1500);
    return () => {
      obs.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [drawerIsOpen]);

  // Clear result highlight when not on result context.
  // useLayoutEffect fires synchronously before paint, eliminating the one-frame
  // stale dimming flash that useEffect would cause.
  useLayoutEffect(() => {
    if (!isOnResultRoute) {
      dispatch(setHighlightedResultUuid(null));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnResultRoute]);

  // Step 4: When ?forInterval changes while on Status tab with highlight active,
  // update highlightedIntervalEnding to follow the new column selection.
  useEffect(() => {
    const isOnStatusTab = activeTab === 'status';
    if (!isOnStatusTab) return;
    if (highlightedIntervalEnding === null) return; // highlight is not active
    const forInterval = searchParams.get('forInterval');
    if (forInterval && forInterval !== highlightedIntervalEnding) {
      dispatch(setHighlightedIntervalEnding(forInterval));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, activeTab]);

  // Enter edit mode when ?edit=title is in URL
  useEffect(() => {
    const editParam = searchParams.get('edit');
    if (editParam === 'title' && objective) {
      setIsEditingObjective(true);
      setObjectiveText(objective.text || '');
      // Focus the input after React renders it
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('edit'), objective?.uuid]);

  const handleClose = () => {
    // Close the drawer by removing drawer-related params, preserving the rest.
    // Also strips ?from / ?fromInterval to prevent leaking origin state (mr-3j4t.1).
    setDrawerIsOpen(false);
    dispatch(setHighlightedHumanUuid(null));
    dispatch(setHighlightedObjective(null));
    dispatch(setHighlightedIntervalEnding(null));
    dispatch(setHighlightedResultUuid(null));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('drawer');
      next.delete('objectiveId');
      next.delete('tab');
      next.delete('resultId');
      next.delete('edit');
      next.delete(PARAM_FROM);
      next.delete(PARAM_FROM_INTERVAL);
      return next;
    });
  };

  const handleHighlightToggle = () => {
    if (isOnResultRoute) {
      // Result context: toggle highlight for the result.
      // Always clear objective-related highlight state to avoid stale precedence.
      if (!result) return;
      if (isResultHighlighted) {
        dispatch(setHighlightedResultUuid(null));
      } else {
        dispatch(setHighlightedObjective(null));
        dispatch(setHighlightedIntervalEnding(null));
        dispatch(setHighlightedResultUuid(result.uuid));
      }
      return;
    }

    // Objective context.
    // Always clear result highlight to avoid stale highlightedResultUuid stealing
    // opacity precedence in StatusCell2 when switching from result to objective context.
    if (!objective) return;
    // Detect if we are on the Status tab
    const isOnStatusTab = activeTab === 'status';
    if (isHighlighted) {
      dispatch(setHighlightedObjective(null));
      dispatch(setHighlightedIntervalEnding(null));
      dispatch(setHighlightedResultUuid(null));
    } else {
      dispatch(setHighlightedResultUuid(null));
      dispatch(setHighlightedObjective(objective.uuid));
      // On the Status tab, also highlight the column matching the current ?forInterval
      if (isOnStatusTab) {
        const forInterval = searchParams.get('forInterval');
        dispatch(setHighlightedIntervalEnding(forInterval));
      }
    }
  };

  const handleBadgeCopy = async () => {
    if (!objective?.shortId) return;
    try {
      await navigator.clipboard.writeText(objective.shortId);
      setObjectiveCopiedConfirm(true);
      setTimeout(() => setObjectiveCopiedConfirm(false), 1500);
    } catch {
      // silently fail
    }
  };

  // Remove ?edit=title from URL after save or cancel (preserve all other params)
  const removeEditParam = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('edit');
      return next;
    }, { replace: true });
  };

  const handleObjectiveSave = async () => {
    if (!objective) return;

    const trimmedText = objectiveText.trim();
    if (!trimmedText) {
      console.warn('Cannot save objective with empty text');
      return;
    }

    if (trimmedText !== objective.text) {
      await dispatch(
        updateObjective({
          uuid: objective.uuid,
          updates: { text: trimmedText },
        }),
      );
    }
    setIsEditingObjective(false);
    removeEditParam();
  };

  const handleObjectiveEditCancel = () => {
    setIsEditingObjective(false);
    setObjectiveText(objective?.text || '');
    removeEditParam();
  };

  const handleObjectiveEdit = () => {
    setIsEditingObjective(true);
    setObjectiveText(objective?.text || '');
  };

  // Result header edit handlers
  const handleResultSave = async () => {
    if (!objective || !result) return;

    const trimmed = resultText.trim();
    if (!trimmed) {
      console.warn('Cannot save result with empty text');
      return;
    }

    if (trimmed !== result.text) {
      await dispatch(
        updateObjective({
          uuid: objective.uuid,
          updates: {
            results: objective.results.map((r) =>
              r.uuid === result.uuid ? createResult1({ ...r, text: trimmed }) : r,
            ),
          },
        }),
      );
    }
    setIsEditingResult(false);
  };

  const handleResultEditCancel = () => {
    setIsEditingResult(false);
    setResultText(result?.text || '');
  };

  const handleResultEdit = () => {
    setIsEditingResult(true);
    setResultText(result?.text || '');
  };

  const handleResultBadgeCopy = async () => {
    if (!result?.shortId) return;
    try {
      await navigator.clipboard.writeText(result.shortId);
      setShortIdToastMessage('Copied to clipboard');
    } catch {
      setShortIdToastMessage('Failed to copy');
    }
  };

  // Navigate back to the originating tab from result context.
  // Reads ?from / ?fromInterval to restore the correct tab + interval (mr-3j4t.1).
  // Falls back to tab=info when origin params are absent or invalid.
  const handleBackToObjective = () => {
    const from = searchParams.get(PARAM_FROM);
    const fromInterval = searchParams.get(PARAM_FROM_INTERVAL);
    const { tab, forInterval } = getValidReturnTab({ from, fromInterval });

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      if (forInterval) {
        next.set('forInterval', forInterval);
      } else {
        next.delete('forInterval');
      }
      next.delete('resultId');
      next.delete(PARAM_FROM);
      next.delete(PARAM_FROM_INTERVAL);
      return next;
    });
  };

  // Render the active tab content based on ?tab= param
  const renderTabContent = () => {
    if (isOnResultRoute) {
      // Result context always renders ResultTab
      return <ResultTab />;
    }

    switch (activeTab) {
      case 'status':
        return <StatusTab1 />;
      case 'results':
        return <ResultsTab />;
      case 'descendants':
        return <DescendantsTab />;
      case 'comments':
        return <CommentsTab />;
      case 'settings':
        return <SettingsTab />;
      case 'team':
        return <Map3Team />;
      case 'info':
      default:
        return <ObjectiveInfoTab />;
    }
  };

  return (
    <>
    <Toast
      message={shortIdToastMessage}
      variant="info"
      position="top-right"
      autoClose
      onClose={() => setShortIdToastMessage(null)}
    />
    <Dialog open={drawerIsOpen} onClose={handleClose} className="relative z-10">
      {/* pointer-events-none: clicks pass through to the map grid beneath.
          The capture-phase pointerdown listener on document (see useEffect above)
          detects outside clicks and calls handleClose. Without pointer-events-none
          these divs absorbed every click before it reached the <td>, making
          document.activeElement never the grid cell when onClose fired.
          See mr-aqs.30 for the root cause diagnosis. */}
      <div className="fixed inset-0 pointer-events-none" />

      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              ref={panelRef}
              transition
              className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                <div className="px-4 sm:px-6">
                  <div className="flex items-center justify-end">
                    <div className="flex h-7 items-center">
                      <Button
                        ref={firstFocusableRef}
                        onClick={handleClose}
                        className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 hover:ring-2 hover:ring-indigo-500 hover:ring-offset-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                      </Button>
                    </div>
                  </div>
                  <DialogTitle className="mt-2 space-y-1">
                    {/* Dynamic header based on drawer context */}
                    {isOnResultRoute ? (
                      /* Result context header — matches objective header pattern */
                      <>
                        {/* Single-line header: back icon, entity label, badge, pager */}
                        <div className="text-xs font-medium text-zinc-500 flex items-center gap-2">
                          {/* Back icon — navigates to parent objective Info tab */}
                          {objective && mapShortId && ownerName && objectiveShortId && (
                            <button
                              onClick={handleBackToObjective}
                              title={objective.text}
                              aria-label={getReturnLinkAriaLabel({
                                from: searchParams.get(PARAM_FROM),
                                fromInterval: searchParams.get(PARAM_FROM_INTERVAL),
                              })}
                              className="p-0.5 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-zinc-500 hover:text-zinc-700 flex-shrink-0"
                            >
                              <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                          )}
                          Result
                          {result && (
                            <BadgeButton
                              color="zinc"
                              onClick={handleResultBadgeCopy}
                              aria-label={`Copy result short ID: ${result.shortId}`}
                              title="Click to copy result short ID"
                            >
                              {result.shortId}
                            </BadgeButton>
                          )}
                          {mapShortId && ownerName && (
                            <ResultPager ownerName={ownerName} mapShortId={mapShortId} />
                          )}
                        </div>

                        {/* Editable result title */}
                        <div
                          className="relative p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          onMouseEnter={() => setIsResultActionBarVisible(true)}
                          onMouseLeave={() => setIsResultActionBarVisible(false)}
                        >
                          {isEditingResult ? (
                            <div className="space-y-2">
                              <input
                                ref={resultTitleInputRef}
                                type="text"
                                value={resultText}
                                onChange={(e) => setResultText(e.target.value)}
                                onBlur={handleResultSave}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleResultSave();
                                  } else if (e.key === 'Escape') {
                                    handleResultEditCancel();
                                  }
                                }}
                                className="w-full px-2 py-1 text-base font-semibold leading-6 text-gray-900 rounded border border-gray-300 dark:bg-zinc-700 dark:text-white"
                                autoFocus
                                aria-label="Edit result text"
                              />
                            </div>
                          ) : (
                            <div className="flex justify-between items-start">
                              <div
                                className="text-base font-semibold leading-6 text-gray-900 flex-grow pr-2 flex items-center gap-2"
                                aria-label={`${result?.isKeyResult ? 'Key Result' : 'Result'}: ${result?.text || 'Result not found'}`}
                              >
                                <ResultFlag isKeyResult={!!result?.isKeyResult} />
                                {result?.text || 'Result not found'}
                              </div>
                              <div className="flex-shrink-0">
                                <AddEditDeleteBar1
                                  isVisible={isResultActionBarVisible}
                                  showAdd={false}
                                  showEdit={canEdit}
                                  showDelete={false}
                                  showRemove={false}
                                  showInfo={false}
                                  showHighlight={true}
                                  isHighlighted={isResultHighlighted}
                                  onEdit={handleResultEdit}
                                  onHighlight={handleHighlightToggle}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      /* Objective context header (also used for status tab) */
                      <>
                        <div className="text-xs font-medium text-zinc-500 flex items-center gap-2">
                          Objective
                          {objective?.shortId && (
                            <div className="flex items-center gap-2">
                              <BadgeButton
                                color="zinc"
                                onClick={handleBadgeCopy}
                                aria-label={`Copy objective short ID: ${objective.shortId}`}
                                title="Click to copy objective short ID"
                              >
                                {objective.shortId}
                              </BadgeButton>
                              <span
                                aria-live="polite"
                                className={`text-xs text-green-600 transition-opacity ${objectiveCopiedConfirm ? 'opacity-100' : 'opacity-0'}`}
                              >
                                Copied!
                              </span>
                            </div>
                          )}
                          {mapShortId && ownerName && (
                            <ObjectivePager ownerName={ownerName} mapShortId={mapShortId} />
                          )}
                        </div>
                        <div
                          className="relative p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          onMouseEnter={() => setIsActionBarVisible(true)}
                          onMouseLeave={() => setIsActionBarVisible(false)}
                        >
                          {isEditingObjective ? (
                            <div className="space-y-2">
                              <input
                                ref={titleInputRef}
                                type="text"
                                value={objectiveText}
                                onChange={(e) => setObjectiveText(e.target.value)}
                                onBlur={handleObjectiveSave}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleObjectiveSave();
                                  } else if (e.key === 'Escape') {
                                    handleObjectiveEditCancel();
                                  }
                                }}
                                className="w-full px-2 py-1 text-base font-semibold leading-6 text-gray-900 rounded border border-gray-300 dark:bg-zinc-700 dark:text-white"
                                autoFocus
                              />
                            </div>
                          ) : (
                            <div className="flex justify-between items-start">
                              <div className="text-base font-semibold leading-6 text-gray-900 flex-grow pr-2">
                                {objective?.text || 'Objective not found'}
                              </div>
                              <div className="flex-shrink-0">
                                <AddEditDeleteBar1
                                  isVisible={isActionBarVisible}
                                  showAdd={false}
                                  showEdit={canEdit}
                                  showDelete={false}
                                  showRemove={false}
                                  showInfo={false}
                                  showHighlight={true}
                                  isHighlighted={isHighlighted}
                                  onEdit={handleObjectiveEdit}
                                  onHighlight={handleHighlightToggle}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </DialogTitle>
                </div>
                <div className="relative mt-6 flex-1 px-4 sm:px-6">
                  {/* Context-dependent tab bar */}
                  <DrawerTabBar
                    context={drawerContext}
                    activeTab={activeTab}
                    featureFlags={featureFlags}
                    canViewAllResults={canViewAllResults}
                    canViewComments={canViewComments}
                  />
                  <div className="pt-6" data-testid="drawer-tab-content">
                    {renderTabContent()}
                  </div>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
    </>
  );
}
